<template>
  <div
    class="main-center"
    :class="[
      !formatInfo?.listLine || formatInfo.listLine.length === 0 || !isShowCanvas
        ? 'main-center-table'
        : '',
    ]"
    ref="fabricCanvas"
    :style="{
      height: 'auto',
    }"
  >
    <div
      class="tuiImageEditorBox"
      v-if="canClick && isShowCanvas"
      :style="{
        width: `${canvasInfo.width}px`,
        height: `${canvasInfo.height}px`,
      }"
    >
      <canvas class="tuiImageEditor" ref="tuiImageEditor"></canvas>
    </div>
    <img
      v-else
      :src="fileUrl"
      :formatInfo="JSON.stringify(props.formatInfo)"
      :canClick="canClick"
      class="ql-image test"
      alt=""
    />
  </div>
</template>
  
<script setup>
import {
  ref,
  defineProps,
  watch,
  nextTick,
  onUnmounted,
  onMounted,
  defineExpose,
} from "vue";
import { fabric } from "fabric";
const props = defineProps({
  fileUrl: {
    type: String,
    default: "",
  },
  formatInfo: {
    type: Object || String,
    default: {},
  },
  canClick: {
    type: Boolean,
    default: false,
  },
});
let fileUrl = ref("");
const formatInfo = ref({});
// const showImageUrl = ref(""); // 编译器模式显示的弹窗

const canvasInfo = ref({}); // 画布信息
const boxWidth = ref(0); // 画布容器信息
const isShowCanvas = ref(false); // 是否显示画布

const editorInstance = ref(null); // fabric实例
const newWidth = ref(0); // 画布宽度
let propsTimeoutId = null;
let timeoutId = null;
let resizeObserver = null;
// ---ref---
const fabricCanvas = ref(null); // 画布容器
const tuiImageEditor = ref(null); // 画布

watch(
  [() => props.fileUrl, () => props.formatInfo],
  async () => {
    if (!props.fileUrl) return;
    fileUrl.value = props.fileUrl;
    if (props.canClick) {
      propsDataChange();
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

onMounted(() => {
  // 页面隐藏时保存canvas内容
  document.addEventListener("visibilitychange", handleVisibilitychange);
});
function propsDataChange() {
  if (propsTimeoutId) {
    clearTimeout(propsTimeoutId); // 清除之前的定时器
    propsTimeoutId = null; // 重置定时器ID
  }
  propsTimeoutId = setTimeout(() => {
    isShowCanvas.value = false;
    nextTick(async () => {
      boxWidth.value = fabricCanvas.value?.offsetWidth;
      newWidth.value = Math.round(fabricCanvas.value?.offsetWidth);
      if (props.formatInfo && props.formatInfo?.listLine?.length) {
        formatInfo.value = props.formatInfo || {};
        await initImgEditor();
      } else {
        formatInfo.value = {};
      }
      if (fabricCanvas.value) {
        // 创建 ResizeObserver 实例，并指定回调函数
        resizeObserver = new ResizeObserver(async (entries) => {
          for (let entry of entries) {
            if (
              Math.round(newWidth.value) !== Math.round(entry.contentRect.width)
            ) {
              // 启动定时器，在一段时间后检查是否有更多的宽度变化事件
              if (timeoutId) {
                clearTimeout(timeoutId); // 清除之前的定时器
                timeoutId = null; // 重置定时器ID
              }
              timeoutId = setTimeout(async () => {
                isShowCanvas.value = false;
                boxWidth.value = Math.round(entry.contentRect.width);
                newWidth.value = Math.round(entry.contentRect.width);
                // 如果在定时器到期前没有更多的宽度变化事件，执行内部的异步函数
                await nextTick(async () => {
                  await initImgEditor();
                });
                clearTimeout(timeoutId); // 清除之前的定时器
                timeoutId = null; // 重置定时器ID
              }, 200); // 设置延迟时间
            }
          }
        });
        // 开始观察目标元素的大小变化
        resizeObserver.observe(fabricCanvas.value);
      }

      clearTimeout(propsTimeoutId); // 清除之前的定时器
      propsTimeoutId = null; // 重置定时器ID
    });
  });
}

async function initImgEditor() {
  editorInstance.value?.clear();
  isShowCanvas.value = true;
  await nextTick(() => {
    // 开始绘制
    init();
  });
}
async function init() {
  // 绘制内容
  let imgUrl = props.fileUrl;
  let img = new Image();
  img.onload = function () {
    nextTick(async () => {
      let originalWidth = img.naturalWidth;
      let originalHeight = img.naturalHeight;
      let aspectRatio = originalWidth / originalHeight;
      let maxWidth = boxWidth.value;
      let maxHeight = boxWidth.value;
      maxHeight = maxWidth / aspectRatio;
      maxWidth = maxHeight * aspectRatio;
      canvasInfo.value = {
        originalWidth,
        originalHeight,
        width: maxWidth,
        height: maxHeight,
      };
      if (editorInstance.value) {
        editorInstance.value.clear();
      }
      // 使用 fabric 接管容器 传入canvas的id
      editorInstance.value = new fabric.Canvas(tuiImageEditor.value, {
        width: canvasInfo.value.width, // 画布宽度
        height: canvasInfo.value.height, // 画布高度
        isDrawingMode: false, // 是否开始自由绘制模式
        preserveObjectStacking: false, // 元素对象被选中时保持原有层级
        selectable: false, // 元素是否可选
        hoverCursor: props.canClick ? "pointer" : "default", // 鼠标悬停到线条时的光标样式
      });
      if (formatInfo.value.listLine && formatInfo.value.listLine.length) {
        if (formatInfo.value.width && formatInfo.value.height) {
          // 文档卡的原始宽高
          canvasInfo.value.originalWidth = formatInfo.value.width;
          canvasInfo.value.originalHeight = formatInfo.value.height;
        }
        let scalc = canvasInfo.value.width / canvasInfo.value.originalWidth;
        formatInfo.value.listLine.forEach((line) => {
          let reduceNum = Math.floor(((line.strokeWidth || 20) * scalc) / 2);
          const newX1 = line.startPoint.dx * scalc - reduceNum;
          const newY1 = line.startPoint.dy * scalc - reduceNum;
          const newX2 = line.endPoint.dx * scalc - reduceNum;
          const newY2 = line.endPoint.dy * scalc - reduceNum;
          let path = null;
          if (line.strokeLineCap && line.strokeLineCap === 1) {
            const NodeHandler = fabric.Line;
            path = new NodeHandler([newX1, newY1, newX2, newY2], {
              stroke: hexToRGBa(
                line.color ? line.color : "#FD9C9C",
                props.canClick ? 1 : 0.4
              ),
              strokeWidth: Math.floor((line.strokeWidth || 20) * scalc),
              strokeLineCap: "round",
              hasControls: false,
              hasBorders: true,
              selectable: false,
              borderScaleFactor: 2,
            });
            editorInstance.value.add(path);
          } else {
            const NodeHandler = fabric.Line;
            path = new NodeHandler([newX1, newY1, newX2, newY2], {
              stroke: hexToRGBa(
                line.color ? line.color : "#FD9C9C",
                props.canClick ? 1 : 0.4
              ),
              strokeWidth: Math.floor((line.strokeWidth || 20) * scalc),
              strokeLineCap: "butt",
              hasControls: false,
              hasBorders: true,
              selectable: false,
              borderScaleFactor: 2,
            });
            editorInstance.value.add(path);
          }
          // 自定义碰撞检测
          path.isPointOnLine = function (pointer) {
            return isPointNearLine(
              pointer.x,
              pointer.y,
              this.x1 + reduceNum,
              this.y1 + reduceNum,
              this.x2 + reduceNum,
              this.y2 + reduceNum,
              this.strokeWidth / 2
            );
          };
        });
        // 渲染Canvas
        editorInstance.value.renderAll();
      }

      await fabric.Image.fromURL(
        imgUrl,
        function (image) {
          image.scaleX = editorInstance.value.width / image.width;
          image.scaleY = editorInstance.value.height / image.height;
          editorInstance.value.setBackgroundImage(image);
          editorInstance.value.renderAll();
          watchCanvasOn(true);
        },
        { crossOrigin: "anonymous" }
      );
    });
  };
  img.src = imgUrl;
}
function watchCanvasOn(isOn) {
  if (!editorInstance.value) return;
  if (isOn) {
    editorInstance.value.on("mouse:down", mouseDownHandler);
  } else {
    editorInstance.value.off("mouse:down", mouseDownHandler);
  }
}
function mouseDownHandler(event) {
  if (!props.canClick) return;

  // 获取点击位置的坐标
  const pointer = editorInstance.value.getPointer(event.e);

  // 获取画布上的所有对象
  const objects = editorInstance.value.getObjects();
  let haveChoice = false;
  if (objects && objects.length) {
    // 遍历所有对象
    objects.forEach((object) => {
      // 检查对象是否与点击位置重叠
      if (object.type === "line" && object.isPointOnLine(pointer)) {
        haveChoice = true;
        object.opacity = object.opacity === 0 ? 1 : 0;
      }
    });
    if (haveChoice) {
      // 重新渲染画布
      editorInstance.value.renderAll();
    }
  }
}
function handleVisibilitychange() {
  let pageVisibility = document.visibilityState;
  if (pageVisibility === "hidden") {
    // 隐藏
  } else if (pageVisibility === "visible") {
    propsDataChange();
  }
}
// 颜色转换
function hexToRGBa(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const a = alpha || 1;
  return "rgba(" + r + "," + g + "," + b + "," + a + ")";
}

// 获取绘制后的图片地址
function getFabricUrl() {
  if (editorInstance.value) {
    let url = editorInstance.value.toDataURL({
      format: "png",
      quality: 1.0,
    });
    return url;
  } else {
    return props.fileUrl;
  }
}
// 方法
function pointToLineDistance(px, py, ax, ay, bx, by) {
  // 向量 AP 和 AB
  let APx = px - ax;
  let APy = py - ay;
  let ABx = bx - ax;
  let ABy = by - ay;
  // 向量 AB 的长度的平方
  let AB2 = ABx * ABx + ABy * ABy;
  // 投影点 Q 的参数 t
  let t = (APx * ABx + APy * ABy) / AB2;
  // 确定最近点
  if (t < 0) {
    // 最近点是 A
    var qx = ax;
    var qy = ay;
  } else if (t > 1) {
    // 最近点是 B
    var qx = bx;
    var qy = by;
  } else {
    // 最近点是线段上的投影点 Q
    var qx = ax + t * ABx;
    var qy = ay + t * ABy;
  }
  // 计算距离
  let dx = qx - px;
  let dy = qy - py;
  return Math.sqrt(dx * dx + dy * dy);
}

function isPointNearLine(px, py, ax, ay, bx, by, tolerance) {
  let distance = pointToLineDistance(px, py, ax, ay, bx, by);
  return distance <= tolerance;
}

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  if (propsTimeoutId) {
    clearTimeout(propsTimeoutId); // 清除之前的定时器
    propsTimeoutId = null; // 重置定时器ID
  }
  if (timeoutId) {
    clearTimeout(timeoutId); // 清除之前的定时器
    timeoutId = null; // 重置定时器ID
  }
  if (editorInstance.value) {
    watchCanvasOn(false);
    editorInstance.value.clear();
  }
  document.removeEventListener("visibilitychange", handleVisibilitychange);
});

defineExpose({
  getFabricUrl,
});
</script>
  
  <style lang="scss" scoped>
.main-center {
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  .tuiImageEditorBox {
    width: 100%;
    height: auto;
    :deep(.canvas-container) {
      background-color: transparent;
      overflow: initial;
      position: initial;
      canvas {
        cursor: pointer !important;
      }
    }
  }
}
</style>